import { useFrame } from '@react-three/fiber';
import { useNaphthaleneStore, NaphthaleneStage } from '../store/useNaphthaleneStore';
import LabRoom from '../../lab/shared/LabRoom';
import ClampStand from '../../lab/shared/ClampStand';
import { Beaker } from '../../lab/shared/Beaker';
import { TripodStand } from '../../lab/shared/TripodStand';
import { WireGauze } from '../../lab/shared/WireGauze';
import { BunsenBurner } from '../../lab/shared/BunsenBurner';
import { BoilingTube } from '../../lab/shared/BoilingTube';
import { Thermometer } from '../../lab/shared/Thermometer';
import ConicalFlask from '../../lab/shared/ConicalFlask';
import { PouringCylinder, CylinderWaterStream } from '../../lab/shared/PouringCylinder';
import {
  SpatulaNaphthalenePour,
  ThermometerInsertion,
  BoilingTubeImmersion,
  BoilingTubeTransfer,
} from './NaphthaleneAnimations';

export function NaphthaleneScene3D() {
  const labStage = useNaphthaleneStore(s => s.labStage);
  const isHeating = useNaphthaleneStore(s => s.isHeating);
  const currentTemperature = useNaphthaleneStore(s => s.currentTemperature);
  const isStirring = useNaphthaleneStore(s => s.isStirring);
  const waterBathTemp = useNaphthaleneStore(s => s.waterBathTemp);
  const heatIntensity = useNaphthaleneStore(s => s.heatIntensity);
  const meltFraction = useNaphthaleneStore(s => s.meltFraction);
  const tick = useNaphthaleneStore(s => s.tick);
  const toggleHeating = useNaphthaleneStore(s => s.toggleHeating);
  const setHeatIntensity = useNaphthaleneStore(s => s.setHeatIntensity);
  const simulationSpeed = useNaphthaleneStore(s => s.simulationSpeed);

  const isAddingNaphthalene = useNaphthaleneStore(s => s.isAddingNaphthalene);
  const addNaphthaleneProgress = useNaphthaleneStore(s => s.addNaphthaleneProgress);
  const isInsertingThermometer = useNaphthaleneStore(s => s.isInsertingThermometer);
  const thermometerProgress = useNaphthaleneStore(s => s.thermometerProgress);
  const isPouringWater = useNaphthaleneStore(s => s.isPouringWater);
  const pourProgress = useNaphthaleneStore(s => s.pourProgress);
  const isImmersingTube = useNaphthaleneStore(s => s.isImmersingTube);
  const immersionProgress = useNaphthaleneStore(s => s.immersionProgress);
  const isTransferring = useNaphthaleneStore(s => s.isTransferring);
  const transferProgress = useNaphthaleneStore(s => s.transferProgress);

  useFrame((_, delta) => {
    tick(delta * simulationSpeed);
  });

  const matterState: 'Solid' | 'Solid+Liquid' | 'Liquid' =
    meltFraction >= 1 ? 'Liquid' : meltFraction > 0 ? 'Solid+Liquid' : 'Solid';

  const tubeVisibleStages: NaphthaleneStage[] = [
    'naphthalene-added', 'thermometer-inserted', 'water-poured',
    'tube-immersed', 'heating', 'melting', 'transfer-ready',
  ];
  const showStaticTube =
    (tubeVisibleStages.includes(labStage) || isAddingNaphthalene) &&
    !isImmersingTube && !isTransferring;

  const thermometerVisibleStages: NaphthaleneStage[] = [
    'thermometer-inserted', 'water-poured',
    'tube-immersed', 'heating', 'melting', 'transfer-ready',
  ];

  const showStaticThermometer =
    thermometerVisibleStages.includes(labStage) &&
    !isInsertingThermometer;

  const beakerHasWater =
    labStage !== 'setup' &&
    labStage !== 'naphthalene-added' &&
    labStage !== 'thermometer-inserted';
  const beakerWaterLevel = beakerHasWater ? 0.4 : 0;

  const showFlask = labStage === 'cooling' || labStage === 'freezing' || labStage === 'completed';

  const staticTubeY = 1.4;

  // Naphthalene fills as the spatula pour progresses; afterward holds at 0.4
  const tubeSubstanceLevel = isAddingNaphthalene
    ? 0.4 * addNaphthaleneProgress
    : labStage === 'setup'
      ? 0
      : 0.4;

  return (
    <group>
      <LabRoom />

      <group position={[0, -0.585, -0.2]}>
        <TripodStand position={[0, 0, 0]} scale={0.6} />
        <WireGauze position={[0, 1.5 * 0.6, 0]} scale={0.6} />

        <Beaker
          position={[0, (1.5 * 0.6) + 0.01, 0]}
          scale={1.5}
          waterLevel={beakerWaterLevel}
          waterTemp={waterBathTemp}
        />

        <BunsenBurner
          position={[0, 0, 0]}
          scale={4}
          flameState={isHeating ? 'heating' : 'off'}
          intensity={heatIntensity}
          onToggle={toggleHeating}
          onAdjust={() => setHeatIntensity(heatIntensity >= 1.0 ? 0.2 : heatIntensity + 0.4)}
        />

        <ClampStand visible={true} position={[-0.68, 0, 0]} rotation={[0, Math.PI, 0]} armHeight={1.6} />

        {showStaticTube && (
          <BoilingTube
            position={[0, staticTubeY, 0]}
            scale={1}
            stateOfMatter={matterState}
            substanceLevel={tubeSubstanceLevel}
          />
        )}

        {showStaticThermometer && (
          <Thermometer
            position={[0, 1.5, 0]}
            temperature={currentTemperature}
            isStirring={isStirring}
          />
        )}

        {showFlask && (
          <ConicalFlask
            position={[0.7, 0, 0]}
            scale={0.3}
            showLiquid={true}
            liquidColor={matterState === 'Liquid' ? '#f0f8ff' : '#fdfdfd'}
            liquidLabel="C₁₀H₈"
            liquidFillRatio={0.55}
            isReacting={false}
            isPaused={true}
          />
        )}

        <SpatulaNaphthalenePour active={isAddingNaphthalene} progress={addNaphthaleneProgress} />

        <ThermometerInsertion
          active={isInsertingThermometer}
          progress={thermometerProgress}
          temperature={currentTemperature}
          isStirring={isStirring}
        />

        <PouringCylinder
          isPouring={isPouringWater}
          pourProgress={pourProgress}
          showAtRest={labStage === 'thermometer-inserted' && !isPouringWater}
          liquidLevelMlAtRest={50}
          liquidColor="#f0f9ff"
        />
        <CylinderWaterStream isPouring={isPouringWater} pourProgress={pourProgress} />

        <BoilingTubeImmersion
          active={isImmersingTube}
          progress={immersionProgress}
          substanceLevel={0.4}
          stateOfMatter={matterState}
        />

        <BoilingTubeTransfer active={isTransferring} progress={transferProgress} />
      </group>
    </group>
  );
}
